import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronLeft, Clock, ExternalLink, Heart, History, MessageCircle, Music, Play, Share2, Users } from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import SEO from "../components/SEO";

const FeaturesPage = () => {
  const navigate = useNavigate();
  
  const playerFeatures = [
    { 
      icon: <Play className="w-8 h-8" />, 
      title: "Live Video Streaming", 
      description: "Broadcast live video alongside your audio stream. Let your listeners see behind the scenes."
    },
    { 
      icon: <Music className="w-8 h-8" />, 
      title: "Now Playing Display", 
      description: "Beautiful album art, track info, and progress bar. Your listeners always know what's playing."
    },
    { 
      icon: <Clock className="w-8 h-8" />, 
      title: "Up Next Queue", 
      description: "Show upcoming tracks with timing info. Build anticipation for what's coming next."
    },
    { 
      icon: <History className="w-8 h-8" />, 
      title: "Recently Played", 
      description: "Full history of played tracks with timestamps. Listeners can discover music they missed."
    },
    { 
      icon: <MessageCircle className="w-8 h-8" />, 
      title: "Live Chat", 
      description: "Real-time chat for your community. Engage with listeners and build connections."
    },
    { 
      icon: <Heart className="w-8 h-8" />, 
      title: "Song Requests", 
      description: "Let listeners request songs and dedicate them to friends. Interactive radio experience."
    },
    { 
      icon: <Share2 className="w-8 h-8" />, 
      title: "Social Sharing", 
      description: "One-click sharing to social media. Help your channel grow organically."
    },
    { 
      icon: <Users className="w-8 h-8" />, 
      title: "Top Fans Leaderboard", 
      description: "Recognize your most loyal listeners. Gamify the listening experience."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO title="Features" description="Explore all features of DJsandMCMedia™ — white-label players, AutoDJ, live video streaming, playlist scheduling, song requests, live chat, audio visualizer, and more." />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white hover:text-purple-400 transition">
            <ChevronLeft className="w-5 h-5" />
            Back to Home
          </button>
          <a 
            href="https://app.djsandmc.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-xl transition"
          >
            Try Live Demo
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/20 to-black"></div>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-purple-500/20 text-purple-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Premium Radio Player
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              DJ's & MC Live
            </h1>
            <p className="text-xl text-white/60 max-w-2xl mx-auto mb-8">
              Nashville's #1 Internet Radio Experience. A premium web player that brings your channel to life.
            </p>
            <div className="flex items-center justify-center gap-4">
              <a 
                href="https://app.djsandmc.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition flex items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                Launch Player
              </a>
              <button 
                onClick={() => navigate('/#pricing')}
                className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl text-lg font-semibold transition"
              >
                Get Your Channel
              </button>
            </div>
          </div>

          {/* Player Preview */}
          <div className="relative max-w-4xl mx-auto">
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 rounded-3xl blur-2xl opacity-30"></div>
            <div className="relative bg-gray-900 rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_8ef5f57d-55ac-4aed-8db1-e87e55f3d721/artifacts/km8nab6r_image.png"
                alt="DJ's & MC Live Player"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gradient-to-b from-black to-purple-950/20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">Packed With Features</h2>
          <p className="text-center text-white/60 mb-16 max-w-2xl mx-auto">
            Everything your listeners need for an immersive radio experience
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {playerFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:border-purple-500/50"
              >
                <div className="w-14 h-14 bg-purple-500/20 rounded-xl flex items-center justify-center text-purple-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlight Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Live Chat Feature */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                Community
              </span>
              <h3 className="text-3xl font-bold text-white mb-4">Real-Time Live Chat</h3>
              <p className="text-white/60 mb-6">
                Build a community around your channel. Listeners can chat with each other and DJs in real-time. 
                Moderate conversations, highlight messages, and create an interactive experience.
              </p>
              <ul className="space-y-3">
                {['Real-time messaging', 'User presence indicators', 'Emoji support', 'Moderation tools'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <Check className="w-5 h-5 text-green-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                  <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">G</div>
                  <div>
                    <p className="text-white font-medium">Gary NU Hayden <span className="text-white/40 text-sm">12:17 PM</span></p>
                    <p className="text-white/70">Hey!!!</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-black/40 rounded-xl p-4">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">DJ</div>
                  <div>
                    <p className="text-white font-medium">DJ Smooth <span className="text-white/40 text-sm">12:18 PM</span></p>
                    <p className="text-white/70">Who ready to vibe? 🎤🔥</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Request Feature */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-8">
              <div className="grid grid-cols-4 gap-4 text-center">
                {[
                  { icon: <History className="w-6 h-6" />, label: 'History' },
                  { icon: <Music className="w-6 h-6" />, label: 'Request' },
                  { icon: <Heart className="w-6 h-6" />, label: 'Dedicate' },
                  { icon: <Share2 className="w-6 h-6" />, label: 'Share' }
                ].map((item, i) => (
                  <div key={i} className="bg-black/40 rounded-xl p-4">
                    <div className="text-purple-400 flex justify-center mb-2">{item.icon}</div>
                    <p className="text-white/70 text-sm">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="inline-block bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-sm font-medium mb-4">
                Interactive
              </span>
              <h3 className="text-3xl font-bold text-white mb-4">Song Requests & Dedications</h3>
              <p className="text-white/60 mb-6">
                Let your listeners participate! They can request songs from your library and dedicate them to 
                friends. Turn passive listening into active engagement.
              </p>
              <ul className="space-y-3">
                {['Browse & request songs', 'Personal dedications', 'Top fans recognition', 'Social sharing'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/80">
                    <Check className="w-5 h-5 text-purple-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-purple-950/20 to-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Elevate Your Channel?</h2>
          <p className="text-white/60 mb-8 max-w-2xl mx-auto">
            Get the DJ's & MC Live player experience for your own channel. 
            Start streaming to the world today.
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <a 
              href="https://app.djsandmc.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-xl text-lg font-semibold transition flex items-center gap-2"
            >
              <ExternalLink className="w-5 h-5" />
              View Live Demo
            </a>
            <button 
              onClick={() => navigate('/#pricing')}
              className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-4 rounded-xl text-lg font-semibold transition"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-white/40 text-sm">Powered by DJsandMCMedia™</p>
        </div>
      </footer>
    </div>
  );
};


export default FeaturesPage;
